import { createAction, createAsyncThunk, createSlice, SerializedError } from "@reduxjs/toolkit";
import { LoadingState } from "http/model";
import { getHorseData } from "appredux/actions/horseActions";
import { Horse } from "api/horses/types";
import { horsesService } from "api/horses";
import { generateNewMicrochipId } from "layouts/horses/add-horse/utils/generateNewMicrochipId";

export interface IHorsesState {
  status: LoadingState;
  statusForImport: LoadingState;
  error: null | SerializedError;
  horses: Horse[];
  horsesForImport: Horse[];
}

const HORSES_INITIAL_STATE: IHorsesState = {
  status: LoadingState.initial,
  statusForImport: LoadingState.initial,
  error: null,
  horses: [],
  horsesForImport: [],
};

export const fetchHorsesForImport = createAsyncThunk(
  "horses/fetchHorsesForImport",
  async (farmcode: string) => {
    const response = await horsesService.getHorsesForImport(farmcode);

    const horses = await Promise.all(
      response.map(async (horse: Horse) => ({
        ...horse,
        microchip:
          horse.microchip ||
          (await generateNewMicrochipId(
            `${horse.name}-${horse.sire}-${horse.dam}-${horse.farm}-${horse.year_of_birth}-${horse.sex}-${horse.year_of_birth}`
          )),
      }))
    );

    return horses;
  }
);

export const resetHorsesForImport = createAction("horses/resetHorsesForImport");

const horsesSlice = createSlice({
  name: "horses",
  initialState: HORSES_INITIAL_STATE,
  reducers: {},
  extraReducers: (builder) => {
    // getAppData
    builder.addCase(getHorseData.pending, (state: IHorsesState) => {
      state.status = LoadingState.loading;
    });
    builder.addCase(getHorseData.fulfilled, (state: IHorsesState, action) => {
      state.horses = action.payload;
      state.status = LoadingState.loaded;
    });
    builder.addCase(getHorseData.rejected, (state: IHorsesState, action) => {
      state.status = LoadingState.failed;
      state.error = action.error;
    });

    builder.addCase(fetchHorsesForImport.pending, (state: IHorsesState) => {
      state.statusForImport = LoadingState.loading;
    });
    builder.addCase(fetchHorsesForImport.fulfilled, (state: IHorsesState, action) => {
      state.horsesForImport = action.payload;
      state.statusForImport = LoadingState.loaded;
    });
    builder.addCase(fetchHorsesForImport.rejected, (state: IHorsesState, action) => {
      state.statusForImport = LoadingState.failed;
      state.error = action.error;
    });
    builder.addCase(resetHorsesForImport, (state: IHorsesState) => {
      state.horsesForImport = [];
      state.statusForImport = LoadingState.initial;
    });
  },
  selectors: {
    selectHorses: (sliceState) => sliceState.horses,
    selectLoading: (sliceState) => sliceState.status,
    selectHorsesForImport: (sliceState) => sliceState.horsesForImport,
    selectLoadingForImport: (sliceState) => sliceState.statusForImport,
  },
});

export const { selectHorses, selectLoading, selectHorsesForImport, selectLoadingForImport } =
  horsesSlice.selectors;

export default horsesSlice.reducer;
